'use client';

import { Card, CardHeader, CardContent } from '@packages/shared/src/ui/Card/Card';
import { useIntl } from 'react-intl';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'urql';
import { PlaceSampleOrderDocument } from '@packages/gql/generated/shopping/PlaceSampleOrderDocument';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import type { PropsWithChildren } from 'react';
import { PrimaryButton } from '../../components/PrimaryButton/PrimaryButton.client';
import { SecondaryButton } from '../../components/SecondaryButton/SecondaryButton.client';
import { SampleOrderContent } from '../../components/SampleOrderContent/SampleOrderContent';
import { getSampleImageUrl } from '../../utils/getSampleImageUrl/getSampleImageUrl';
import type { PersonalDataFormSchema, SampleDataValueSchema } from '../schema';
import type { SampleOrderData, SetStep } from '../../types';
import type { SampleEntry, SampleGroup } from '../types';
import { SampleOrderSteps } from '../types';
import { sharedMessages } from '../shared.messages';
import { ActionButtonsWrapper } from '../../components/ActionButtonsWrapper/ActionButtonsWrapper';

/* GraphQL */ `
  mutation PlaceSampleOrder($sampleOrderDto: SampleOrderDTOInput) {
    placeSampleOrder(sampleOrderDto: $sampleOrderDto)
  }
`;

const EditButton = ({ actionHandler }: { actionHandler(): void }) => (
  <Button
    variant={'icon-flat'}
    onClick={actionHandler}
    onKeyDown={(event) => {
      event.preventDefault();
      if (event.key === 'Enter') {
        actionHandler();
      }
    }}
  >
    <span className="icon-emp-edit size-1.5 cursor-pointer" />
  </Button>
);

const DefaultCard = ({
  children,
  headerText,
  editAction,
  withMargin = false,
}: PropsWithChildren & { headerText: string; editAction(): void; withMargin?: boolean }) => (
  <Card {...(withMargin && { className: 'mt-2' })}>
    <CardHeader hasDivider className="px-2 py-1.5">
      <div className="flex w-full flex-row items-center justify-between">
        <p className="text-size-h5">{headerText}</p>

        <EditButton actionHandler={editAction} />
      </div>
    </CardHeader>
    <CardContent className="p-2">{children}</CardContent>
  </Card>
);

export type ReviewProps = SetStep & SampleOrderData;

export const Review = ({ setStep, sampleOrderData }: ReviewProps) => {
  const { formatMessage } = useIntl();

  const { getValues } = useFormContext();
  const [{ fetching }, placeSampleOrder] = useMutation(PlaceSampleOrderDocument);
  const { addNotification } = useNotification();

  const formData: PersonalDataFormSchema = getValues('inputData');
  const selectedSamples = sampleOrderData.reduce(
    (result: SampleEntry[], currentValue: SampleGroup) => [
      ...result,
      ...currentValue.values.filter((value) =>
        getValues('sampleData.values').some((item: SampleDataValueSchema) => item.id === value.id),
      ),
    ],
    [] satisfies SampleEntry[],
  );

  const submitData = async () => {
    const { data } = await placeSampleOrder({ sampleOrderDto: getValues() });

    if (data?.placeSampleOrder === true) {
      setStep(SampleOrderSteps.Finished);
    } else {
      addNotification({
        message: formatMessage({
          defaultMessage:
            'Entschuldige, irgendetwas ist schief gelaufen. Probiere es später noch einmal.',
          id: 'sampleOrder.review.submitError',
        }),
        severity: 'warning',
        duration: 35000,
      });
    }
  };

  return (
    <SampleOrderContent
      pageContent={
        <div>
          <DefaultCard
            editAction={() => setStep(SampleOrderSteps.PersonalDataForm)}
            headerText={formatMessage(sharedMessages.deliveryAddress)}
          >
            <p className="text-size-body2">
              {formatMessage(formData.salutation === 'Mr' ? sharedMessages.mr : sharedMessages.ms)}
              <br />
              {formData.name} {formData.surname}
              <br />
              {formData.street} {formData.houseNumber}
              <br />
              {formData.postalCode} {formData.location}
            </p>
          </DefaultCard>
          <DefaultCard
            withMargin
            editAction={() => setStep(SampleOrderSteps.SelectSamples)}
            headerText={formatMessage({
              defaultMessage: 'Gewählte Muster',
              id: 'sampleOrder.review.selectedSamples',
            })}
          >
            <div className="flex flex-col gap-1">
              {selectedSamples.map(({ id, image, name }) => (
                <div className="flex flex-row flex-nowrap items-center gap-1" key={id}>
                  <div className="overflow-hidden rounded border">
                    <Image
                      src={getSampleImageUrl(image)}
                      alt={name}
                      width="36"
                      height="36"
                      unoptimized
                    />
                  </div>

                  <div className="text-size-body2">{name}</div>
                </div>
              ))}
            </div>
          </DefaultCard>
        </div>
      }
      actionBarContent={
        <ActionButtonsWrapper
          primary={
            <PrimaryButton
              actionHandler={submitData}
              disabled={fetching === true}
              text={formatMessage({
                defaultMessage: 'Musterbestellung abschicken',
                id: 'sampleOrder.review.button.next',
              })}
            />
          }
          secondary={
            <SecondaryButton
              actionHandler={() => {
                setStep(SampleOrderSteps.PersonalDataForm);
              }}
              text={formatMessage({
                defaultMessage: 'Zurück zur Lieferadresse',
                id: 'sampleOrder.review.button.back',
              })}
            />
          }
        />
      }
    />
  );
};
